/**
 * Vite-freie State-Helper für Dokumentenliste + ARKO-Kasten.
 * Unit-Tests: node --experimental-strip-types (kein constants.ts / import.meta.env).
 */

/** Favoriten dieser Liste zuerst, jede Datei nur einmal. Fremde Treffer bleiben draußen. */
export function portalDocsFavoritesFirst<T extends { id: string }>(
  docs: readonly T[],
  favorites: readonly { pb_record_id?: string | null }[],
): { favorites: T[]; rest: T[] } {
  const ids = new Set(
    favorites.map((row) => (row.pb_record_id || '').trim()).filter(Boolean),
  );
  const picked = new Set<string>();
  const fav: T[] = [];
  const rest: T[] = [];
  for (const doc of docs) {
    if (picked.has(doc.id)) continue;
    picked.add(doc.id);
    if (ids.has(doc.id)) fav.push(doc);
    else rest.push(doc);
  }
  return { favorites: fav, rest };
}

export function buildMetadataOnlySynthesisContext(meta: {
  title: string;
  nc_path: string | null;
}): string {
  const title = meta.title.trim() || 'Dokument';
  const source = meta.nc_path?.trim() || '(unbekannt)';
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    'Hinweis: Volltext noch nicht geladen — bitte den Tab einmal aktivieren oder den Auszug in den Chat kopieren.',
  ].join('\n');
}

/**
 * Chat-Kasten / Synthese: Titel und NC-Pfad immer aus aktueller Meta (PB/openDocuments).
 * Cache darf nur Extract liefern — sonst springt Rename nach Speichern auf den alten display_name zurück.
 */
export function portalSelectedDocumentFromMeta(
  meta: { documentId: string; title: string; nc_path: string | null },
  cached?: {
    documentContext?: string | null;
    documentExtractFingerprint?: string | null;
    title?: string;
    nc_path?: string | null;
    fullExtractText?: string | null;
    extractPages?: number | null;
    extractRawChars?: number | null;
    extractTruncated?: boolean;
    extractSource?: string | null;
  },
): {
  documentId: string;
  title: string;
  nc_path: string | null;
  documentContext: string;
  documentExtractFingerprint?: string;
  fullExtractText?: string;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
} {
  const title = meta.title.trim() || cached?.title?.trim() || 'Dokument';
  const nc_path = meta.nc_path ?? cached?.nc_path ?? null;
  const entry: {
    documentId: string;
    title: string;
    nc_path: string | null;
    documentContext: string;
    documentExtractFingerprint?: string;
    fullExtractText?: string;
    extractPages?: number;
    extractRawChars?: number;
    extractTruncated?: boolean;
    extractSource?: string;
  } = {
    documentId: meta.documentId,
    title,
    nc_path,
    documentContext: cached?.documentContext?.trim() || buildMetadataOnlySynthesisContext({ title, nc_path }),
  };
  if (cached?.documentExtractFingerprint) {
    entry.documentExtractFingerprint = cached.documentExtractFingerprint;
  }
  const full = cached?.fullExtractText?.trim();
  if (full) entry.fullExtractText = full;
  if (cached?.extractPages && cached.extractPages > 0) entry.extractPages = cached.extractPages;
  if (cached?.extractRawChars && cached.extractRawChars > 0) entry.extractRawChars = cached.extractRawChars;
  if (cached?.extractTruncated) entry.extractTruncated = true;
  if (cached?.extractSource?.trim()) entry.extractSource = cached.extractSource.trim();
  return entry;
}

/** Nach Folder-API rename: Liste lokal patchen — kein loadProtokolle-Revert auf stale PB. */
export function applyPortalDocumentRenameToProtokolle<
  T extends { id: string; filename: string; nextcloud_path?: string },
>(
  rows: T[],
  args: { pb_record_id: string; display_name: string; nextcloud_path?: string },
): T[] {
  return rows.map((row) =>
    row.id === args.pb_record_id
      ? {
          ...row,
          filename: args.display_name,
          nextcloud_path: args.nextcloud_path ?? row.nextcloud_path,
        }
      : row,
  );
}

export function applyPortalDocumentRenameToShortcuts<
  T extends { kind: string; pb_record_id?: string; title: string; nextcloud_path?: string },
>(
  items: T[],
  args: { pb_record_id: string; title: string; nextcloud_path?: string },
): T[] {
  return items.map((row) => {
    if (row.kind !== 'document' || row.pb_record_id !== args.pb_record_id) return row;
    return {
      ...row,
      title: args.title,
      nextcloud_path: args.nextcloud_path ?? row.nextcloud_path,
    };
  });
}
